import { StoreAttributeFieldsBag } from './store.model';

export type StoreAttributeValueType = | 'Boolean' | 'SingleSelect' | 'MultiSelect'| 'Text';
export type DisplayColor = | 'blue' | 'red' | 'green' | 'orange'| 'purple'| 'teal'| 'brown'| 'grey';
export const DISPLAY_COLORS: DisplayColor[] = ['blue', 'red', 'green', 'orange','purple', 'teal','brown','grey'];

export const CATEGORY_COLOR_MAP: Record<DisplayColor, string> = {
  blue: '#1976d2',
  red: '#d32f2f',
  green: '#388e3c',
  orange: '#f57c00',
  purple: '#7b1fa2',
  teal: '#00897b',
  brown: '#6d4c41',
  grey: '#757575',
};

export const CATEGORY_BACKGROUND_COLOR_MAP: Record<DisplayColor, string> = {
  blue: '#e3f2fd',
  red: '#fce4ec',
  green: '#e8f5e9',
  orange: '#fff3e0',
  purple: '#f3e5f5',
  teal: '#e0f2f1',
  brown: '#efebe9',
  grey: '#f5f5f5',
};

export const VALUE_TYPE_LABELS: Record<StoreAttributeValueType, string> = {
  Boolean: 'Boolean (Yes/No)',
  SingleSelect: 'Dropdown (Single Select)',
  MultiSelect: 'Multi-Select',
  Text: 'Text',
};

export const VALUE_TYPE_DIALOG_LABELS: Record<StoreAttributeValueType, string> = {
  Boolean: 'Boolean',
  SingleSelect: 'Dropdown',
  MultiSelect: 'Multi-Select',
  Text: 'Text',
};

export const STORE_ATTRIBUTE_VALUE_TYPES: StoreAttributeValueType[] = [
  'Boolean',
  'SingleSelect',
  'MultiSelect',
  'Text',
];

export const ALLOWED_VALUE_SOURCE_FIELDS = [
  'City',
  'State',
  'Country',
  'Division',
  'Banner',
] as const;

export type AllowedValueSourceField = (typeof ALLOWED_VALUE_SOURCE_FIELDS)[number];

export function isAllowedValueSourceField(value: string | null | undefined): boolean {
  const trimmed = value?.trim();
  if (!trimmed) {
    return true;
  }

  const normalized = trimmed.toLowerCase();
  return ALLOWED_VALUE_SOURCE_FIELDS.some((field) => field.toLowerCase() === normalized);
}

export interface StoreAttributeDetail {
  Id?: string;
  ClientKey?: string;
  UserId?: string;
  AttributeTypeName: string;
  ValueType: StoreAttributeValueType;
  ValueSourceField?: string;
  FilterEnabled: boolean;
  Values: string[];
}

export interface StoreAttributeDomain {
  DomainId?: string;
  Detail?: StoreAttributeDetail;
  CategoryId?: string;
  CategoryName?: string;
}

export interface StoreAttributeCategory {
  Id?: string;
  ClientKey?: string;
  UserId?: string;
  CategoryName: string;
  Description?: string;
  DisplayColor?: string;
  DisplayOrder?: number;
}

export interface StoreAttributeDomainSavePayload {
  Detail: StoreAttributeDetail;
  CategoryId: string;
}

export interface StoreAttributeValueViewModel {
  label: string;
  storeCount: number;
}

export interface StoreAttributeTypeViewModel {
  domain: StoreAttributeDomain;
  id: string;
  name: string;
  valueType: StoreAttributeValueType;
  valueTypeLabel: string;
  sourceField?: string;
  filterEnabled: boolean;
  values: StoreAttributeValueViewModel[];
}

export interface StoreAttributeCategoryViewModel {
  category: StoreAttributeCategory;
  id: string;
  name: string;
  color: DisplayColor;
  description?: string;
  displayOrder: number;
  attributeTypes: StoreAttributeTypeViewModel[];
  collapsedSummary: string;
}

export interface StoreAttributeSummaryStats {
  categoryCount: number;
  typeCount: number;
  totalValues: number;
  activeInFilters: number;
}

export interface StoreAttributeTypesViewModel {
  categories: StoreAttributeCategoryViewModel[];
  stats: StoreAttributeSummaryStats;
  isLoading: boolean;
  totalRecords: number;
}

export function sortStoreAttributeCategories(categories: StoreAttributeCategory[]): StoreAttributeCategory[] {
  return [...categories].sort((a, b) => (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0));
}

export function buildOrderedCategoryIds(categories: StoreAttributeCategory[], targetCategoryId: string, positionIndex: number): string[] {
  const sorted = sortStoreAttributeCategories(categories);
  const withoutTarget = sorted
    .filter((category) => category.Id && category.Id !== targetCategoryId)
    .map((category) => category.Id!);
  const insertIndex = Math.max(0, Math.min(positionIndex, withoutTarget.length));
  const result = [...withoutTarget];
  result.splice(insertIndex, 0, targetCategoryId);
  return result;
}

export function getNextCategoryDisplayOrder(categories: StoreAttributeCategory[]): number {
  if (!categories.length) {
    return 0;
  }
  return Math.max(...categories.map((c) => c.DisplayOrder ?? 0)) + 1;
}

export function buildClientKey(name: string): string {
  const trimmed = (name ?? '').trim();
  if (!trimmed) {
    return '';
  }

  return trimmed
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .map((word, index) => {
      const lower = word.toLowerCase();
      if (index === 0) {
        return lower;
      }
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join('');
}

export function readCategoryDisplayColor(category: Partial<StoreAttributeCategory> & { displayColor?: string }): string | undefined {
  return category.DisplayColor ?? category.displayColor;
}

export function normalizeDisplayColor(color?: string): DisplayColor {
  const normalized = (color ?? 'blue').toLowerCase() as DisplayColor;
  return DISPLAY_COLORS.includes(normalized) ? normalized : 'blue';
}

export function supportsManagedValues(valueType: StoreAttributeValueType): boolean {
  return valueType === 'Boolean' || valueType === 'SingleSelect' || valueType === 'MultiSelect';
}

export function showsSourceField(valueType: StoreAttributeValueType): boolean {
  return valueType === 'SingleSelect' || valueType === 'MultiSelect';
}

export function showsAllowedValuesEditor(valueType: StoreAttributeValueType): boolean {
  return valueType === 'SingleSelect' || valueType === 'MultiSelect';
}

export function canEditAttributeValues(valueType: StoreAttributeValueType, sourceField?: string): boolean {
  return showsAllowedValuesEditor(valueType) && !(sourceField?.trim());
}

export function canEnableAsFilter(valueType: StoreAttributeValueType): boolean {
  return valueType !== 'Text';
}

export function isDuplicateAllowedValue(values: string[], candidate: string): boolean {
  const trimmed = candidate.trim();
  if (!trimmed) {
    return false;
  }
  return values.some((value) => value === trimmed);
}

export function hasDuplicateAllowedValues(values: string[]): boolean {
  const trimmed = values.map((value) => value.trim()).filter(Boolean);
  return new Set(trimmed).size !== trimmed.length;
}

export function buildDomainSavePayloadFromType(
  attributeType: StoreAttributeTypeViewModel,
  overrides?: Partial<StoreAttributeDetail>,
): StoreAttributeDomainSavePayload {
  const detail = attributeType.domain.Detail!;
  const valueType = overrides?.ValueType ?? detail.ValueType;
  const filterEnabled = canEnableAsFilter(valueType)
    ? overrides?.FilterEnabled ?? detail.FilterEnabled
    : false;

  return {
    CategoryId: attributeType.domain.CategoryId ?? '',
    Detail: {
      ...detail,
      ...overrides,
      ValueType: valueType,
      FilterEnabled: filterEnabled,
    },
  };
}

export interface StoreAttributeEditFieldViewModel {
  clientKey: string;
  name: string;
  valueType: StoreAttributeValueType;
  options: string[];
  isReadOnly: boolean;
  sourceField?: string;
}

export interface StoreAttributeEditCategoryViewModel {
  name: string;
  color: DisplayColor;
  colorHex: string;
  fields: StoreAttributeEditFieldViewModel[];
}

export interface StoreAttributeFilterFieldViewModel {
  attributeId: string;
  clientKey: string;
  name: string;
  categoryName: string;
  valueType: StoreAttributeValueType;
  options: string[];
  selectOptions?: StoreSourceSelectOption[];
  categoryColor: DisplayColor;
  categoryColorHex: string;
  sourceField?: string;
}

export interface StoreAttributeFilterCategoryViewModel {
  name: string;
  color: DisplayColor;
  colorHex: string;
  fields: StoreAttributeFilterFieldViewModel[];
}

export interface VersionAttributeFilterSelection {
  clientKey: string;
  label: string;
  displayValue: string;
  categoryColor: DisplayColor;
}

export interface VersionInheritedAttributeTag {
  label: string;
  value: string;
  coveragePercent?: number;
  categoryColorHex: string;
}

export type VersionAttributeMatchType = 'full' | 'partial';

export interface VersionAttributeSearchResult {
  versionName: string;
  versionId?: string;
  storeCount: number;
  matchPercent: number;
  matchType: VersionAttributeMatchType;
  inheritedAttributes: VersionInheritedAttributeTag[];
}

export interface VersionAttributeSelectionState {
  selectedResults: VersionAttributeSearchResult[];
  totalStores: number;
}

export interface VersionAttributeSearchSummary {
  matchingVersions: number;
  fullMatch: number;
  partialMatch: number;
  totalStores: number;
}

export function buildFilterEnabledAttributesViewModel(
  domains: StoreAttributeDomain[],
  categories: StoreAttributeCategory[],
): StoreAttributeFilterCategoryViewModel[] {
  const filterDomains = domains.filter(
    (domain) => domain.Detail?.FilterEnabled && domain.Detail?.ClientKey?.trim(),
  );

  const sortedCategories = [...categories].sort(
    (a, b) => (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0),
  );

  const categorizedCategoryIds = new Set(
    sortedCategories.map((category) => category.Id).filter(Boolean),
  );

  const buildFields = (
    categoryId: string,
    categoryName: string,
    color: DisplayColor,
    colorHex: string,
  ): StoreAttributeFilterFieldViewModel[] =>
    filterDomains
      .filter((domain) => domain.CategoryId === categoryId)
      .map((domain) => mapFilterFieldViewModel(domain, categoryName, color, colorHex))
      .sort((a, b) => a.name.localeCompare(b.name));

  const result = sortedCategories
    .map((category) => {
      const color = normalizeDisplayColor(readCategoryDisplayColor(category));
      const colorHex = CATEGORY_COLOR_MAP[color];
      const fields = buildFields(category.Id ?? '', category.CategoryName, color, colorHex);

      if (!fields.length) {
        return null;
      }

      return {
        name: category.CategoryName,
        color,
        colorHex,
        fields,
      };
    })
    .filter((category): category is StoreAttributeFilterCategoryViewModel => category !== null);

  const uncategorizedFields = filterDomains
    .filter(
      (domain) =>
        !domain.CategoryId || !categorizedCategoryIds.has(domain.CategoryId),
    )
    .map((domain) =>
      mapFilterFieldViewModel(domain, 'Other', 'grey', CATEGORY_COLOR_MAP.grey),
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  if (uncategorizedFields.length) {
    result.push({
      name: 'Other',
      color: 'grey',
      colorHex: CATEGORY_COLOR_MAP.grey,
      fields: uncategorizedFields,
    });
  }

  return result;
}

function mapFilterFieldViewModel(
  domain: StoreAttributeDomain,
  categoryName: string,
  categoryColor: DisplayColor,
  categoryColorHex: string,
): StoreAttributeFilterFieldViewModel {
  const detail = domain.Detail!;

  return {
    attributeId: detail.Id ?? domain.DomainId ?? '',
    clientKey: detail.ClientKey!,
    name: detail.AttributeTypeName,
    categoryName,
    valueType: detail.ValueType,
    options: detail.Values ?? [],
    categoryColor,
    categoryColorHex,
    sourceField: detail.ValueSourceField?.trim() || undefined,
  };
}

type AttributeFieldValuesBag = {
  Values?: Record<string, unknown>;
  [key: string]: unknown;
};

type AttributeFieldValuesSource = {
  AttributeFields?: AttributeFieldValuesBag;
  AttribuiteFields?: AttributeFieldValuesBag;
  Values?: Record<string, unknown>;
};

export function extractAttributeFieldValues(detailOrBag?: AttributeFieldValuesSource | AttributeFieldValuesBag): Record<string, unknown> {
  if (!detailOrBag) {
    return {};
  }

  const source = detailOrBag as AttributeFieldValuesSource;
  const bag: AttributeFieldValuesBag =
    source.AttributeFields ?? source.AttribuiteFields ?? (detailOrBag as AttributeFieldValuesBag);

  if (bag.Values && typeof bag.Values === 'object' && !Array.isArray(bag.Values)) {
    return bag.Values;
  }

  const { Values, ...flat } = bag;
  return flat;
}

export function toAttributeBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (value === 'Yes' || value === 'true') {
    return true;
  }

  if (value === 'No' || value === 'false') {
    return false;
  }

  return false;
}

export function buildStoreAttributesEditViewModel(domains: StoreAttributeDomain[], categories: StoreAttributeCategory[]): StoreAttributeEditCategoryViewModel[] {
  const sortedCategories = [...categories].sort((a, b) => (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0));

  const categorizedCategoryIds = new Set(
    sortedCategories.map((category) => category.Id).filter(Boolean),
  );

  const buildFields = (categoryId: string): StoreAttributeEditFieldViewModel[] =>
    domains
      .filter((domain) => domain.CategoryId === categoryId && domain.Detail?.ClientKey?.trim())
      .map((domain) => mapEditFieldViewModel(domain))
      .sort((a, b) => a.name.localeCompare(b.name));

  const result = sortedCategories
    .map((category) => {
      const color = normalizeDisplayColor(readCategoryDisplayColor(category));
      const fields = buildFields(category.Id ?? '');

      if (!fields.length) {
        return null;
      }

      return {
        name: category.CategoryName,
        color,
        colorHex: CATEGORY_COLOR_MAP[color],
        fields,
      };
    })
    .filter((category): category is StoreAttributeEditCategoryViewModel => category !== null);

  const uncategorizedFields = domains
    .filter(
      (domain) =>
        domain.Detail?.ClientKey?.trim() &&
        (!domain.CategoryId || !categorizedCategoryIds.has(domain.CategoryId)),
    )
    .map((domain) => mapEditFieldViewModel(domain))
    .sort((a, b) => a.name.localeCompare(b.name));

  if (uncategorizedFields.length) {
    result.push({
      name: 'Other',
      color: 'grey',
      colorHex: CATEGORY_COLOR_MAP.grey,
      fields: uncategorizedFields,
    });
  }

  return result;
}

function mapEditFieldViewModel(domain: StoreAttributeDomain): StoreAttributeEditFieldViewModel {
  const detail = domain.Detail!;

  return {
    clientKey: detail.ClientKey!,
    name: detail.AttributeTypeName,
    valueType: detail.ValueType,
    options: detail.Values ?? [],
    isReadOnly: !!detail.ValueSourceField?.trim(),
    sourceField: detail.ValueSourceField?.trim() || undefined,
  };
}

export interface StoreSourceLookupOption {
  Id?: string;
  BannerName?: string;
  DivisionName?: string;
}

export interface StoreSourceBindingContext {
  Detail?: Record<string, unknown>;
  BannerName?: string;
  BannerId?: string;
  DivisionName?: string;
  DivisionId?: string;
  VersionName?: string;
  [key: string]: unknown;
}

export function resolveStoreSourceFieldValue(
  sourceField: string,
  storeDomain: StoreSourceBindingContext,
  banners: StoreSourceLookupOption[] = [],
  divisions: StoreSourceLookupOption[] = [],
): unknown {
  const normalizedKey = sourceField.trim().toLowerCase();
  if (!normalizedKey) {
    return undefined;
  }

  const detail = (storeDomain.Detail ?? {}) as Record<string, unknown>;

  const readProperty = (
    record: Record<string, unknown>,
    key: string,
  ): unknown => {
    const match = Object.keys(record).find((prop) => prop.toLowerCase() === key.toLowerCase());
    return match ? record[match] : undefined;
  };

  const domainValue = readProperty(storeDomain, sourceField.trim());
  if (domainValue !== undefined && domainValue !== null && domainValue !== '') {
    return domainValue;
  }

  const detailValue = readProperty(detail, sourceField.trim());
  if (detailValue !== undefined && detailValue !== null && detailValue !== '') {
    return detailValue;
  }

  if (normalizedKey === 'banner' || normalizedKey === 'bannername') {
    if (storeDomain.BannerName) {
      return storeDomain.BannerName;
    }

    const banner = banners.find((item) => item.Id === storeDomain.BannerId);
    return banner?.BannerName ?? readProperty(detail, 'Banner');
  }

  if (normalizedKey === 'division' || normalizedKey === 'divisionname') {
    if (storeDomain.DivisionName) {
      return storeDomain.DivisionName;
    }

    const division = divisions.find((item) => item.Id === storeDomain.DivisionId);
    return division?.DivisionName;
  }

  if (normalizedKey === 'bannerid') {
    return storeDomain.BannerId ?? readProperty(detail, 'BannerId');
  }

  if (normalizedKey === 'divisionid') {
    return storeDomain.DivisionId ?? readProperty(detail, 'DivisionId');
  }

  return undefined;
}

export interface StoreSourceOptionsContext {
  cities: string[];
  states: string[];
  countries: string[];
  banners: StoreSourceLookupOption[];
  divisions: StoreSourceLookupOption[];
}

export interface StoreSourceSelectOption {
  value: string;
  label: string;
}

function uniqueSortedStrings(values: string[]): string[] {
  return [...new Set(values.filter((value) => value?.trim()))].sort((a, b) =>
    a.localeCompare(b),
  );
}

export function resolveStoreSourceFieldSelectOptions(
  sourceField: string,
  context: StoreSourceOptionsContext,
): StoreSourceSelectOption[] {
  const normalizedKey = sourceField.trim().toLowerCase();
  if (!normalizedKey) {
    return [];
  }

  if (normalizedKey === 'city') {
    return uniqueSortedStrings(context.cities).map((city) => ({
      value: city,
      label: city,
    }));
  }

  if (normalizedKey === 'state') {
    return uniqueSortedStrings(context.states).map((state) => ({
      value: state,
      label: state,
    }));
  }

  if (normalizedKey === 'country') {
    return uniqueSortedStrings(context.countries).map((country) => ({
      value: country,
      label: country,
    }));
  }

  if (
    normalizedKey === 'banner' ||
    normalizedKey === 'bannername' ||
    normalizedKey === 'bannerid'
  ) {
    return context.banners
      .filter((banner) => banner.Id)
      .map((banner) => ({
        value: banner.Id!,
        label: banner.BannerName?.trim() || banner.Id!,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  if (
    normalizedKey === 'division' ||
    normalizedKey === 'divisionname' ||
    normalizedKey === 'divisionid'
  ) {
    return context.divisions
      .filter((division) => division.Id)
      .map((division) => ({
        value: division.Id!,
        label: division.DivisionName?.trim() || division.Id!,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  return [];
}

export function enrichFilterCategoriesWithSourceOptions(
  categories: StoreAttributeFilterCategoryViewModel[],
  context: StoreSourceOptionsContext,
): StoreAttributeFilterCategoryViewModel[] {
  return categories.map((category) => ({
    ...category,
    fields: category.fields.map((field) =>
      enrichFilterFieldWithSourceOptions(field, context),
    ),
  }));
}

function enrichFilterFieldWithSourceOptions(
  field: StoreAttributeFilterFieldViewModel,
  context: StoreSourceOptionsContext,
): StoreAttributeFilterFieldViewModel {
  if (!field.sourceField?.trim()) {
    return field;
  }

  const selectOptions = resolveStoreSourceFieldSelectOptions(
    field.sourceField,
    context,
  );

  if (!selectOptions.length) {
    return field;
  }

  return {
    ...field,
    selectOptions,
    options: selectOptions.map((option) => option.value),
  };
}

export function coerceAttributeFieldValue(
  value: unknown,
  valueType: StoreAttributeValueType,
  options: string[] = [],
): unknown {
  if (value === undefined || value === null) {
    if (valueType === 'Boolean') {
      return false;
    }

    if (valueType === 'MultiSelect') {
      return [];
    }

    return null;
  }

  if (valueType === 'Boolean') {
    return toAttributeBoolean(value);
  }

  if (valueType === 'MultiSelect') {
    return parseMultiSelectValue(value);
  }

  if (valueType === 'SingleSelect') {
    const selected = Array.isArray(value) ? value[0] : value;
    if (selected === undefined || selected === null || selected === '') {
      return null;
    }

    const selectedText = String(selected);
    if (options.length && !options.includes(selectedText)) {
      return selectedText;
    }

    return selectedText;
  }

  return String(value);
}

function parseMultiSelectValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => (item == null ? '' : String(item)))
      .filter((item) => item.length > 0);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }

    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .map((item) => (item == null ? '' : String(item)))
            .filter((item) => item.length > 0);
        }
      } catch {
        // fall through to single-value handling
      }
    }

    return [trimmed];
  }

  return value == null ? [] : [String(value)];
}

export function serializeAttributeFieldValue(
  value: unknown,
  valueType: StoreAttributeValueType,
): string | string[] {
  if (valueType === 'Boolean') {
    return toAttributeBoolean(value) ? 'true' : 'false';
  }

  if (valueType === 'MultiSelect') {
    return parseMultiSelectValue(value);
  }

  return String(value ?? '');
}

export function buildAttributeFieldPayloadKey(attributeName: string): string {
  return (attributeName ?? '').trim();
}

export function resolveClientKeyForAttributePayloadKey(
  payloadKey: string,
  categories: StoreAttributeEditCategoryViewModel[],
): string | undefined {
  for (const category of categories) {
    for (const field of category.fields) {
      if (buildAttributeFieldPayloadKey(field.name) === payloadKey) {
        return field.clientKey;
      }
    }
  }

  return undefined;
}

export function mapAttributePayloadToClientValues(
  payload: Record<string, unknown>,
  categories: StoreAttributeEditCategoryViewModel[],
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  categories.forEach((category) => {
    category.fields.forEach((field) => {
      const payloadKey = buildAttributeFieldPayloadKey(field.name);

      if (payloadKey in payload) {
        result[field.clientKey] = payload[payloadKey];
      } else if (field.clientKey in payload) {
        result[field.clientKey] = payload[field.clientKey];
      }
    });
  });

  return result;
}

export function buildAttributeFieldsPayload(
  attributeValues: Record<string, unknown>,
  categories: StoreAttributeEditCategoryViewModel[],
  sourceMappedKeys: Set<string>,
): StoreAttributeFieldsBag {
  const payload: StoreAttributeFieldsBag = {};

  categories.forEach((category) => {
    category.fields.forEach((field) => {
      if (field.sourceField || sourceMappedKeys.has(field.clientKey)) {
        return;
      }

      if (!(field.clientKey in attributeValues)) {
        return;
      }

      const payloadKey = buildAttributeFieldPayloadKey(field.name);
      payload[payloadKey] = serializeAttributeFieldValue(
        attributeValues[field.clientKey],
        field.valueType,
      );
    });
  });

  return payload;
}

export interface VersionAttributeFilterPair {
  attributeName: string;
  valueSourceField: string;
  value: string;
}

export function resolveAttributeFilterName(
  field: StoreAttributeFilterFieldViewModel,
): string {
  const name = field.name?.trim() ?? '';
  const valueSourceField = field.sourceField?.trim() ?? '';

  if (!valueSourceField) {
    return buildAttributeFieldPayloadKey(name);
  }

  return name;
}

export function resolveFilterFieldDisplayName(
  field: StoreAttributeFilterFieldViewModel,
): string {
  const key = field.sourceField?.trim().toLowerCase();
  const labels: Record<string, string> = {
    banner: 'Banner',
    bannername: 'Banner',
    bannerid: 'Banner',
    division: 'Division',
    divisionname: 'Division',
    divisionid: 'Division',
    city: 'City',
    state: 'State',
    country: 'County',
    county: 'County',
  };

  if (key && labels[key]) {
    return labels[key];
  }

  return field.name.replace(/Name$/i, '').trim() || field.name;
}

export function resolveAttributeFilterKey(
  field: StoreAttributeFilterFieldViewModel,
): string {
  return field.attributeId?.trim() ?? '';
}

export function buildVersionAttributeFiltersPayload(
  values: Record<string, unknown>,
  fields: Iterable<StoreAttributeFilterFieldViewModel>,
): VersionAttributeFilterPair[] {
  const pairs: VersionAttributeFilterPair[] = [];

  for (const field of fields) {
    const attributeName = resolveAttributeFilterName(field);
    if (!attributeName) {
      continue;
    }

    const valueSourceField = field.sourceField?.trim() ?? '';
    const value = values[field.clientKey];

    if (field.valueType === 'Boolean') {
      if (!value) {
        continue;
      }

      pairs.push({ attributeName, valueSourceField, value: 'true' });
      continue;
    }

    if (field.valueType === 'MultiSelect') {
      const selected = Array.isArray(value) ? value : [];
      for (const item of selected) {
        const text = String(item).trim();
        if (!text) {
          continue;
        }

        pairs.push({ attributeName, valueSourceField, value: text });
      }
      continue;
    }

    if (field.valueType === 'Text') {
      const text = value == null ? '' : String(value).trim();
      if (!text) {
        continue;
      }

      pairs.push({ attributeName, valueSourceField, value: text });
      continue;
    }

    if (value === null || value === undefined || value === '') {
      continue;
    }

    pairs.push({ attributeName, valueSourceField, value: String(value) });
  }

  return pairs;
}

export interface VersionAttributeSearchApiItem {
  Id?: string;
  VersionName?: string;
  ClientKey?: string;
  TotalStoresCount?: number;
  MatchedStoresCount?: number;
  MatchPercent?: number;
  IsFullMatch?: boolean;
  totalStoresCount?: number;
  matchedStoresCount?: number;
  matchPercent?: number;
  isFullMatch?: boolean;
  InheritedAttributes?: VersionInheritedAttributeTag[];
  inheritedAttributes?: VersionInheritedAttributeTag[];
}

export interface VersionAttributeConflictBreakdownCell {
  attributeName: string;
  matchedCount: number;
  totalCount: number;
}

export interface VersionAttributeConflictBreakdownRow {
  groupKey: string;
  headerName: string;
  totalCount: number;
  cells: VersionAttributeConflictBreakdownCell[];
}

export interface VersionAttributeConflictBreakdown {
  groupBy: string;
  attributeColumns: string[];
  rows: VersionAttributeConflictBreakdownRow[];
}

export interface VersionAttributeConflictPercentBarSegment {
  value: string;
  count?: number;
  percent: number;
}

export interface VersionAttributeConflictPercentBar {
  attributeName: string;
  percent: number;
  displayMode: string;
  segments: VersionAttributeConflictPercentBarSegment[];
}

export interface VersionAttributeConflictValueCoverage {
  name: string;
  value: string;
  category: string;
  color: DisplayColor;
  colorHex: string;
  backgroundColorHex: string;
  percent: number;
}

export interface VersionAttributeConflictDetail {
  totalStoresCount: number;
  breakdown: VersionAttributeConflictBreakdown;
  percentBars: VersionAttributeConflictPercentBar[];
  allAttributes: VersionAttributeConflictValueCoverage[];
  filteredAttributes: VersionAttributeConflictValueCoverage[];
  notFilteredAttributes: VersionAttributeConflictValueCoverage[];
}

export interface VersionAttributeConflictDetailApiItem {
  TotalStoresCount?: number;
  totalStoresCount?: number;
  Breakdown?: VersionAttributeConflictBreakdownApiItem;
  breakdown?: VersionAttributeConflictBreakdownApiItem;
  PercentBars?: VersionAttributeConflictPercentBarApiItem[];
  percentBars?: VersionAttributeConflictPercentBarApiItem[];
  AllAttributes?: VersionAttributeConflictValueCoverageApiItem[];
  allAttributes?: VersionAttributeConflictValueCoverageApiItem[];
  FilteredAttributes?: VersionAttributeConflictValueCoverageApiItem[];
  filteredAttributes?: VersionAttributeConflictValueCoverageApiItem[];
  NotFilteredAttributes?: VersionAttributeConflictValueCoverageApiItem[];
  notFilteredAttributes?: VersionAttributeConflictValueCoverageApiItem[];
}

interface VersionAttributeConflictBreakdownApiItem {
  GroupBy?: string;
  groupBy?: string;
  AttributeColumns?: string[];
  attributeColumns?: string[];
  Rows?: VersionAttributeConflictBreakdownRowApiItem[];
  rows?: VersionAttributeConflictBreakdownRowApiItem[];
}

interface VersionAttributeConflictBreakdownRowApiItem {
  GroupKey?: string;
  groupKey?: string;
  HeaderName?: string;
  headerName?: string;
  TotalCount?: number;
  totalCount?: number;
  Cells?: VersionAttributeConflictBreakdownCellApiItem[];
  cells?: VersionAttributeConflictBreakdownCellApiItem[];
}

interface VersionAttributeConflictBreakdownCellApiItem {
  AttributeName?: string;
  attributeName?: string;
  MatchedCount?: number;
  matchedCount?: number;
  TotalCount?: number;
  totalCount?: number;
}

interface VersionAttributeConflictPercentBarApiItem {
  AttributeName?: string;
  attributeName?: string;
  Percent?: number;
  percent?: number;
  DisplayMode?: string;
  displayMode?: string;
  Segments?: VersionAttributeConflictPercentBarSegmentApiItem[];
  segments?: VersionAttributeConflictPercentBarSegmentApiItem[];
}

interface VersionAttributeConflictPercentBarSegmentApiItem {
  Value?: string;
  value?: string;
  Count?: number | null;
  count?: number | null;
  Percent?: number;
  percent?: number;
}

interface VersionAttributeConflictValueCoverageApiItem {
  Name?: string;
  name?: string;
  Value?: string;
  value?: string;
  Category?: string;
  category?: string;
  Color?: string;
  color?: string;
  Percent?: number;
  percent?: number;
}

function mapConflictBreakdown(
  item?: VersionAttributeConflictBreakdownApiItem,
): VersionAttributeConflictBreakdown {
  return {
    groupBy: item?.GroupBy ?? item?.groupBy ?? 'State',
    attributeColumns: item?.AttributeColumns ?? item?.attributeColumns ?? [],
    rows: (item?.Rows ?? item?.rows ?? []).map((row) => ({
      groupKey: row.GroupKey ?? row.groupKey ?? '',
      headerName: row.HeaderName ?? row.headerName ?? '',
      totalCount: row.TotalCount ?? row.totalCount ?? 0,
      cells: (row.Cells ?? row.cells ?? []).map((cell) => ({
        attributeName: cell.AttributeName ?? cell.attributeName ?? '',
        matchedCount: cell.MatchedCount ?? cell.matchedCount ?? 0,
        totalCount: cell.TotalCount ?? cell.totalCount ?? 0,
      })),
    })),
  };
}

function mapConflictValueCoverages(
  items?: VersionAttributeConflictValueCoverageApiItem[],
): VersionAttributeConflictValueCoverage[] {
  return (items ?? []).map((item) => {
    const color = normalizeDisplayColor(item.Color ?? item.color);
    return {
      name: item.Name ?? item.name ?? '',
      value: item.Value ?? item.value ?? '',
      category: item.Category ?? item.category ?? '',
      color,
      colorHex: CATEGORY_COLOR_MAP[color],
      backgroundColorHex: CATEGORY_BACKGROUND_COLOR_MAP[color],
      percent: item.Percent ?? item.percent ?? 0,
    };
  });
}

export function mapVersionAttributeConflictDetail(
  item: VersionAttributeConflictDetailApiItem,
): VersionAttributeConflictDetail {
  return {
    totalStoresCount: item.TotalStoresCount ?? item.totalStoresCount ?? 0,
    breakdown: mapConflictBreakdown(item.Breakdown ?? item.breakdown),
    percentBars: (item.PercentBars ?? item.percentBars ?? []).map((bar) => ({
      attributeName: bar.AttributeName ?? bar.attributeName ?? '',
      percent: bar.Percent ?? bar.percent ?? 0,
      displayMode: bar.DisplayMode ?? bar.displayMode ?? '',
      segments: (bar.Segments ?? bar.segments ?? []).map((segment) => ({
        value: segment.Value ?? segment.value ?? '',
        count: segment.Count ?? segment.count ?? undefined,
        percent: segment.Percent ?? segment.percent ?? 0,
      })),
    })),
    allAttributes: mapConflictValueCoverages(item.AllAttributes ?? item.allAttributes),
    filteredAttributes: mapConflictValueCoverages(item.FilteredAttributes ?? item.filteredAttributes),
    notFilteredAttributes: mapConflictValueCoverages(item.NotFilteredAttributes ?? item.notFilteredAttributes),
  };
}

export interface PlainVersionSearchItem {
  Id: string;
  VersionName: string;
  storesCount?: number;
}

export function mapPlainVersionsToAttributeSearchResults(
  versions: PlainVersionSearchItem[],
): VersionAttributeSearchResult[] {
  return versions.map((version) => ({
    versionName: version.VersionName ?? '',
    versionId: version.Id,
    storeCount: version.storesCount ?? 0,
    matchPercent: 100,
    matchType: 'full' as const,
    inheritedAttributes: [],
  }));
}

export function mapVersionsToAttributeSearchResults(
  versions: VersionAttributeSearchApiItem[],
): VersionAttributeSearchResult[] {
  const results = versions.map((version) => {
    const totalStores = version.TotalStoresCount ?? version.totalStoresCount ?? 0;
    const matchedStores = version.MatchedStoresCount ?? version.matchedStoresCount ?? totalStores;
    const serverMatchPercent = version.MatchPercent ?? version.matchPercent;
    const matchPercent = serverMatchPercent != null
      ? serverMatchPercent
      : totalStores > 0
        ? Math.round((matchedStores / totalStores) * 100)
        : 0;
    const serverFullMatch = version.IsFullMatch ?? version.isFullMatch;
    const matchType: VersionAttributeMatchType =
      serverFullMatch != null
        ? (serverFullMatch ? 'full' : 'partial')
        : totalStores > 0 && matchedStores >= totalStores
          ? 'full'
          : 'partial';

    return {
      versionName: version.VersionName ?? '',
      versionId: version.Id,
      storeCount: totalStores,
      matchPercent,
      matchType,
      inheritedAttributes: version.InheritedAttributes ?? version.inheritedAttributes ?? [],
    };
  });

  return results.sort((a, b) => {
    if (a.matchType === 'full' && b.matchType !== 'full') {
      return -1;
    }
    if (a.matchType !== 'full' && b.matchType === 'full') {
      return 1;
    }
    return b.matchPercent - a.matchPercent;
  });
}
